// ─── Research Interpretation Engine ──────────────────────────────────────────
// Claude interprets REAL computed outputs — never fabricates numbers.
// The AI's role: synthesise, explain, compare literature, discuss implications.
// The AI's forbidden role: invent β, p-values, R², F-statistics, or diagnostics.

import { formatResultsForInterpretation } from './statisticsEngine'
import { streamAnthropic, generateAnthropic } from '../../lib/anthropic'

// All inference goes through the backend AI proxy. This module used to take an
// apiKey argument and call api.anthropic.com from the browser, which is how a
// production key ended up in the public bundle. There is no key here now.

async function callClaude(prompt, streamCallback) {
  const opts = {
    feature:   'research_chat',
    maxTokens: 4000,
    messages:  [{ role: 'user', content: prompt }],
  }
  return streamCallback
    ? streamAnthropic({ ...opts, onChunk: streamCallback })
    : generateAnthropic(opts)
}

// ── Prompt builders ────────────────────────────────────────────────────────────

const STRICT_INSTRUCTION = `CRITICAL RULE: Every statistical value in your response (β, p, R², t, F, n, df, alpha, etc.) MUST be taken directly from the COMPUTED RESULTS block below. You MUST NOT change, round differently, or invent ANY numerical value. If a value is not in the results, do not state it.`

const PERSONA = `You are a senior quantitative researcher with 20+ years of experience interpreting statistical findings in published doctoral theses. Write in a confident, analytical academic voice. Use occasional hedging where results warrant it (e.g., "tentatively suggests", "warrants caution"). Never use: "furthermore", "moreover", "to summarise", "it is worth noting". Prefer: "What these results indicate is…", "A notable pattern emerges…", "The diagnostic evidence suggests…", "In practical terms…"`

function buildRegressionInterpretationPrompt(olsResult, thesis, chapterContext) {
  const resultsBlock = formatResultsForInterpretation([olsResult])

  return `${PERSONA}

${STRICT_INSTRUCTION}

THESIS CONTEXT:
Title: "${thesis.title || ''}"
Domain: ${thesis.domain || ''} | Geography: ${thesis.geographic_focus || ''}
Methodology: ${thesis.methodology || ''}

CHAPTER CONTEXT: ${chapterContext || 'Results/Findings chapter'}

COMPUTED RESULTS (DO NOT MODIFY THESE NUMBERS):
${resultsBlock}

TASK: Write a doctoral-level interpretation of these OLS regression results for inclusion in the thesis. Structure your response as flowing academic prose covering:

1. OVERALL MODEL FIT: Interpret R², adjusted R², F-statistic, and what the model explains about ${olsResult.dv}. Comment on model adequacy.

2. COEFFICIENT INTERPRETATION: For each predictor, interpret the unstandardised coefficient (direction, magnitude, practical significance), p-value significance, and 95% confidence interval. Be specific about what each β means in context.

3. NON-SIGNIFICANT FINDINGS: If any predictors are non-significant, interpret this substantively — do NOT dismiss. Discuss what it means for the hypotheses.

4. DIAGNOSTIC EVALUATION: Interpret VIF values for multicollinearity, Breusch-Pagan result for heteroscedasticity, and Durbin-Watson for autocorrelation. Conclude on regression assumption adherence.

5. PRACTICAL IMPLICATIONS: Briefly discuss what these findings mean for ${thesis.geographic_focus || 'the study context'}.

Use ONLY the exact numbers from the COMPUTED RESULTS block above. Write 450–600 words.`
}

function buildCorrelationInterpretationPrompt(corrResult, thesis, chapterContext) {
  const resultsBlock = formatResultsForInterpretation([corrResult])

  return `${PERSONA}

${STRICT_INSTRUCTION}

THESIS CONTEXT:
Title: "${thesis.title || ''}"
Domain: ${thesis.domain || ''} | Geography: ${thesis.geographic_focus || ''}

CHAPTER CONTEXT: ${chapterContext || 'Bivariate analysis / preliminary findings'}

COMPUTED RESULTS (DO NOT MODIFY THESE NUMBERS):
${resultsBlock}

TASK: Write a doctoral-level interpretation of this correlation matrix for inclusion in the thesis. Cover:

1. STRONGEST ASSOCIATIONS: Identify the strongest bivariate correlations and their substantive meaning.

2. UNEXPECTED OR WEAK CORRELATIONS: Discuss correlations that are weaker or in the opposite direction from hypothesised — what does this suggest?

3. MULTICOLLINEARITY CONCERN: Note any r > 0.7 between predictors and discuss implications for regression.

4. PRELIMINARY SUPPORT: Which hypothesised relationships find preliminary bivariate support?

Use ONLY the exact numbers from the COMPUTED RESULTS block above. Write 300–400 words.`
}

function buildDescriptiveInterpretationPrompt(descResult, thesis, variables) {
  const lines = Object.entries(descResult).map(([vname, s]) =>
    `${vname}: N=${s.n} (${s.n_missing} missing), M=${s.mean}, SD=${s.std}, range=[${s.min}, ${s.max}], skewness=${s.skewness}, kurtosis=${s.kurtosis}`
  )

  return `${PERSONA}

${STRICT_INSTRUCTION}

THESIS CONTEXT:
Title: "${thesis.title || ''}"
Domain: ${thesis.domain || ''} | Geography: ${thesis.geographic_focus || ''}
Sample size: ${Object.values(descResult)[0]?.n || 'N/A'}

COMPUTED RESULTS (DO NOT MODIFY THESE NUMBERS):
=== Descriptive Statistics ===
${lines.join('\n')}

TASK: Write a doctoral-level presentation of descriptive statistics for the sample. Cover:

1. SAMPLE OVERVIEW: Total N, missing data rates, and what the sample represents.

2. VARIABLE DISTRIBUTIONS: For each variable, comment on central tendency, spread, and distribution shape (skewness, kurtosis). Flag any non-normal distributions that may affect inferential tests.

3. NOTABLE PATTERNS: Any noteworthy observations about ranges, outliers, or scale use patterns (for Likert items).

Use ONLY the exact numbers above. Write 280–380 words.`
}

function buildCronbachInterpretationPrompt(cronbachResult, thesis) {
  const resultsBlock = formatResultsForInterpretation([cronbachResult])

  return `${PERSONA}

${STRICT_INSTRUCTION}

THESIS CONTEXT:
Title: "${thesis.title || ''}"
Domain: ${thesis.domain || ''}

COMPUTED RESULTS (DO NOT MODIFY THESE NUMBERS):
${resultsBlock}

TASK: Write a doctoral-level interpretation of Cronbach's alpha reliability analysis. Cover:

1. OVERALL RELIABILITY: Interpret the alpha value against established thresholds (Nunnally, 1978; Hair et al., 2019). State clearly whether the scale achieves acceptable reliability.

2. ITEM ANALYSIS: Comment on item-total correlations. Flag any items with low correlations that may be undermining the scale.

3. ALPHA IF DELETED: Discuss whether removing any item would meaningfully improve reliability.

4. VALIDITY CAVEAT: Note that alpha is a measure of internal consistency, not construct validity.

Use ONLY the exact numbers above. Write 220–300 words.`
}

// ── Public API ─────────────────────────────────────────────────────────────────

/**
 * Interpret OLS regression results with Claude.
 * Returns academic prose interpreting REAL computed outputs.
 */
export async function interpretOLS(olsResult, thesis, chapterContext, streamCallback) {
  const prompt = buildRegressionInterpretationPrompt(olsResult, thesis, chapterContext)
  return callClaude(prompt, streamCallback)
}

/**
 * Interpret correlation matrix results.
 */
export async function interpretCorrelation(corrResult, thesis, chapterContext, streamCallback) {
  const prompt = buildCorrelationInterpretationPrompt(corrResult, thesis, chapterContext)
  return callClaude(prompt, streamCallback)
}

/**
 * Interpret descriptive statistics.
 */
export async function interpretDescriptive(descResult, thesis, variables, streamCallback) {
  const prompt = buildDescriptiveInterpretationPrompt(descResult, thesis, variables)
  return callClaude(prompt, streamCallback)
}

/**
 * Interpret Cronbach alpha.
 */
export async function interpretCronbach(cronbachResult, thesis, streamCallback) {
  const prompt = buildCronbachInterpretationPrompt(cronbachResult, thesis)
  return callClaude(prompt, streamCallback)
}

/**
 * Build a complete findings chapter section from multiple computed results.
 * Passes ALL real outputs to Claude in one structured prompt.
 */
export async function buildFindingsSection(allResults, thesis, sectionTitle, streamCallback) {
  const resultsBlock = formatResultsForInterpretation(allResults)

  const prompt = `${PERSONA}

${STRICT_INSTRUCTION}

THESIS CONTEXT:
Title: "${thesis.title || ''}"
Domain: ${thesis.domain || ''} | Geography: ${thesis.geographic_focus || ''}
Methodology: ${thesis.methodology || ''}

SECTION TO WRITE: "${sectionTitle || 'Results and Analysis'}"

COMPUTED RESULTS (ALL REAL — DO NOT MODIFY ANY NUMBER):
${resultsBlock}

TASK: Write a complete doctoral-level findings section integrating all computed results above. The writing must:

- Present results systematically (descriptives → correlations → regression → diagnostics)
- Reference specific computed values using exact numbers from above
- Interpret non-significant findings substantively, not dismissively
- Connect findings to the stated hypotheses with explicit support/rejection statements
- Discuss practical significance alongside statistical significance
- Note diagnostic concerns where present
- Use APA-style reporting conventions (e.g., F(2, 117) = 14.23, p < .001, R² = .196)
- Maintain a critical, analytical voice throughout — do not merely describe

Write 700–950 words of flowing academic prose, structured with appropriate subheadings.`

  return callClaude(prompt, streamCallback)
}

/**
 * Extract computed statistical values from a result for injection into chapter prompts.
 * Used by ChapterEditorPage to inject real data without hallucination.
 */
export function extractKeyFindings(results) {
  const findings = []

  for (const r of results || []) {
    if (!r) continue

    if (r.coefficients) {
      findings.push({
        type: 'OLS',
        summary: `N=${r.n}, R²=${r.r_squared}, adj-R²=${r.adj_r_squared}, F=${r.f_statistic} (p=${r.f_pvalue})`,
        significantPredictors: Object.entries(r.coefficients)
          .filter(([k, v]) => k !== 'const' && v.significant)
          .map(([k, v]) => `${k} (β=${v.beta}, p=${v.p_value})`),
        nonSignificantPredictors: Object.entries(r.coefficients)
          .filter(([k, v]) => k !== 'const' && !v.significant)
          .map(([k, v]) => `${k} (β=${v.beta}, p=${v.p_value})`),
        label: r.label,
      })
    }

    if (r.alpha !== undefined) {
      findings.push({ type: 'Cronbach', summary: `α=${r.alpha} (${r.reliability})`, label: r.label })
    }

    if (r.f_statistic !== undefined && r.group) {
      findings.push({ type: 'ANOVA', summary: `F=${r.f_statistic} (p=${r.f_pvalue}), η²=${r.eta_squared} (${r.effect_size})`, label: r.label })
    }
  }

  return findings
}
